import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { workspaceApiEndpoints, getErrorMessage } from "@routes/workspace/utils/constants";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { getUserActivePlan } from "@helpers/profileHelper";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";
import type { ChatMessage, ContactsOfferBlock, HermesRootState } from "@containers/Hermes/types";
import ActionCardShell, { SelectedCompaniesChip, resultsHeading } from "./ActionCardShell";
import { tableHeaders } from "@containers/Hermes/utils/tableSegments";
import { toBookmarkCompanies } from "./BookmarkCard";

/**
 * "Add to workspace" as an action card, like Bookmark and Request contacts: the card above the composer
 * for the rows an answer shows, and a dialog for rows the user ticked. It is the right-hand side of the
 * workspace modal Search Companies uses — Add as (Buyer / Supplier) and Add to (a new list or an existing
 * one) — without that modal's left-hand list of the companies being added, since the selection is already
 * on screen. Workspaces are keyed on the master company id, like bookmarks.
 *
 * With the "hermes" source the backend covers the new list's last 2 years and extracts its search phrase
 * from the user's questions, which are sent from here.
 */

type ProspectType = "buyer" | "supplier";
type WorkspaceItem = { id?: string | number; name?: string; prospect_type?: ProspectType | null; companies_count?: number; company_count?: number; updated_at?: string; modified_at?: string; created_at?: string };

const PROSPECT_TYPES: { value: ProspectType; label: string }[] = [
  { value: "buyer", label: "Buyer" },
  { value: "supplier", label: "Supplier" },
];

// The questions sent for the list's search: the conversation up to this answer, not just its last turns.
// A search is often set up early and refined later ("cotton from China" … "only to the US" … "by sea"),
// or settled by a reply to Hermes's clarifying question ("top gun importers" … "global, firearms").
const MAX_QUESTIONS = 10;
// The answer's opening restates the search Hermes ran ("Here are the top global firearms importers … HS
// Chapter 93"); its tables add nothing.
const MAX_ANSWER_CHARS = 800;

const countOf = (workspace: WorkspaceItem) => workspace.companies_count || workspace.company_count || 0;

function updatedLabel(workspace: WorkspaceItem): string {
  const value = workspace.updated_at || workspace.modified_at || workspace.created_at;
  const time = value ? new Date(value).getTime() : NaN;
  if (Number.isNaN(time)) return "Updated recently";
  const days = Math.max(1, Math.floor((Date.now() - time) / (24 * 60 * 60 * 1000)));
  if (days === 1) return "Updated yesterday";
  if (days < 7) return `Updated ${days} days ago`;
  if (days < 14) return "Updated one week ago";
  return "Updated recently";
}

/**
 * Whether the answer's companies are buyers or suppliers, to start "Add as" on it: from the table's own
 * columns ("Exporter" / "Shipper" are suppliers; "Importer" / "Consignee" are buyers), else from the trade
 * direction of the page Hermes was opened from (import → Buyer, export → Supplier). Empty when neither says.
 */
function defaultProspectType(content: string | undefined, tableIndex: number | undefined, search: string): ProspectType | "" {
  const headers = content ? tableHeaders(content, tableIndex ?? 0).join(" ") : "";
  if (/exporter|shipper|supplier|seller/i.test(headers)) return "supplier";
  if (/importer|consignee|buyer/i.test(headers)) return "buyer";
  const direction = new URLSearchParams(search).get("type");
  return direction === "import" ? "buyer" : direction === "export" ? "supplier" : "";
}

/** The user's questions up to the answer `messageId`, oldest first. */
function questionsBefore(messages: ChatMessage[], messageId: number): string[] {
  const index = messages.findIndex((message) => message.id === messageId);
  if (index < 0) return [];
  return messages
    .slice(0, index)
    .filter((message) => message.role === "user" && message.content.trim())
    .slice(-MAX_QUESTIONS)
    .map((message) => message.content.trim());
}

/** An answer's text before its first table or code block, as plain text. */
function answerOpening(message: ChatMessage | undefined): string {
  if (!message || message.role !== "assistant") return "";
  const lines: string[] = [];
  for (const line of message.content.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("|") || trimmed.startsWith("```")) break;
    if (trimmed) lines.push(trimmed);
  }
  return lines
    .join(" ")
    .replace(/[*_#`>]+/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_ANSWER_CHARS);
}

type WorkspaceCardProps = {
  block: ContactsOfferBlock;
  messageId: number;
  onClose: () => void;
  modal?: boolean;
  // Records the answer's result holds, for the heading when opened from the bar beneath the table.
  totalRecords?: number;
  // Opens the full table to choose from every record (given only when the result is bigger than the preview).
  onCustomise?: () => void;
};

const radio = (selected: boolean) => `tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-full tw-border tw-border-solid tw-flex tw-items-center tw-justify-center ${selected ? "tw-border-primary-purple-500" : "tw-border-gray-300"}`;

export default function WorkspaceCard({ block, messageId, onClose, modal, totalRecords, onCustomise }: WorkspaceCardProps) {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const messages = useSelector((state: HermesRootState) => state.hermes.messages);
  const companies = useMemo(() => toBookmarkCompanies(block.companies ?? []), [block.companies]);
  const pickerCompanies = useMemo(() => companies.map((company) => ({ key: company.company_id, name: company.name })), [companies]);
  const selectedKeys = useMemo(() => new Set(companies.map((company) => company.company_id)), [companies]);
  const searchQueries = useMemo(() => questionsBefore(messages, messageId), [messages, messageId]);
  const searchAnswer = useMemo(() => answerOpening(messages.find((message) => message.id === messageId)), [messages, messageId]);

  const [prospectType, setProspectType] = useState<ProspectType | "">(() => defaultProspectType(messages.find((message) => message.id === messageId)?.content, block.table_index ?? block.anchor_table, window.location.search));
  const [tab, setTab] = useState<"create" | "existing">("create");
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | number>("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tab === "create") nameRef.current?.focus();
  }, [tab]);

  // Existing lists are filtered to the chosen type. Before one is picked all lists are fetched, so the
  // tab shows the total, but the list itself stays hidden until a type is chosen.
  useEffect(() => {
    setWorkspaceId("");
    let cancelled = false;
    setLoadingLists(true);
    axios
      .get(workspaceApiEndpoints.getIntelWorkspaces, { params: prospectType ? { prospect_type: prospectType } : {} })
      .then(({ data }) => {
        if (!cancelled) setWorkspaces(data?.workspaces || []);
      })
      .catch((error) => {
        if (cancelled) return;
        Sentry.captureException(error);
        setWorkspaces([]);
        toast.error("Unable to fetch workspaces");
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prospectType]);

  const visibleWorkspaces = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? workspaces.filter((workspace) =>
          String(workspace.name || "")
            .toLowerCase()
            .includes(term)
        )
      : workspaces;
  }, [workspaces, search]);

  const createWorkspace = async (): Promise<string | number> => {
    const name = newName.trim();
    if (!name) return "";
    try {
      const payload: { name: string; source: string; prospect_type?: ProspectType; search_queries?: string[]; search_answer?: string } = { name, source: "hermes" };
      if (prospectType) payload.prospect_type = prospectType;
      if (searchQueries.length) payload.search_queries = searchQueries;
      if (searchAnswer) payload.search_answer = searchAnswer;
      const { data } = await axios.post(workspaceApiEndpoints.createIntelWorkspace, payload);
      const created: WorkspaceItem = data?.workspace || data?.data || data;
      return created?.id ?? "";
    } catch (error) {
      Sentry.captureException(error);
      toast.error(getErrorMessage(error, "Unable to create workspace"));
      return "";
    }
  };

  const handleAdd = async () => {
    if (busy || companies.length === 0 || !prospectType) return;
    setBusy(true);
    try {
      let target: string | number = workspaceId;
      if (tab === "create") {
        target = await createWorkspace();
        if (!target) {
          setBusy(false);
          return;
        }
      }
      if (!target) {
        setBusy(false);
        return;
      }
      const { data } = await axios.post(workspaceApiEndpoints.addIntelWorkspaceCompanies(target), {
        source: "hermes",
        companies: companies.map((company) => ({ company_id: company.company_id, company_name: company.name, address: "", country: company.country || "", company_type: prospectType })),
      });
      const added: number = data?.added_count ?? companies.length;
      const skipped: number = data?.skipped_count ?? 0;
      toast.success(
        <span>
          {added} {added === 1 ? "company" : "companies"} added to workspace.
          {skipped > 0 ? ` ${skipped} already in this list.` : ""} <br />
          <button
            type="button"
            className="tw-bg-transparent tw-border-0 tw-p-0 !tw-text-inherit !tw-font-semibold !tw-underline tw-cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              history.push("/workspace", { workspaceId: target });
              toast.dismiss();
            }}>
            View workspace
          </button>
        </span>,
        { closeOnClick: false }
      );
      trademoTrackingEvents(trackingEventDictionary.workspaceAddCompanies, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), source: "hermes", prospectType, companiesCount: companies.length });
      dispatch(getSidebarCountData());
      onClose();
    } catch (error) {
      Sentry.captureException(error);
      toast.error(getErrorMessage(error, "Unable to add companies to workspace"));
      setBusy(false);
    }
  };

  const discard = () => {
    if (!busy) onClose();
  };

  const tabClass = (active: boolean) => `tw-pb-2 tw-px-0 tw-border-0 tw-border-b-2 tw-border-solid tw-bg-transparent tw-text-sm tw-cursor-pointer ${active ? "tw-text-gray-900 tw-border-primary-purple-500" : "tw-text-gray-500 tw-border-transparent"}`;

  return (
    <ActionCardShell modal={modal} title={modal ? "Add to workspace" : resultsHeading(totalRecords ?? companies.length)} primaryLabel="Add to workspace" busyLabel="Adding…" busy={busy} primaryDisabled={companies.length === 0 || !prospectType || (tab === "existing" && !workspaceId) || (tab === "create" && !newName.trim())} onPrimary={handleAdd} onDiscard={discard}>
      {/* A selection the user ticked in the table is not repeated back to them. */}
      {modal ? null : <SelectedCompaniesChip companies={pickerCompanies} selectedKeys={selectedKeys} onCustomise={onCustomise} />}

      <p className="tw-m-0 tw-mt-4 tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900">Add as</p>
      <div className="tw-flex tw-gap-3 tw-max-w-[458px]">
        {PROSPECT_TYPES.map((option) => {
          const selected = prospectType === option.value;
          return (
            <button key={option.value} type="button" disabled={busy} onClick={() => setProspectType(option.value)} className={`tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-sm tw-text-gray-900 tw-cursor-pointer ${selected ? "tw-border-primary-purple-500 tw-bg-primary-purple-50" : "tw-border-gray-200 tw-bg-white"}`}>
              <span className={radio(selected)}>{selected ? <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-purple-500" /> : null}</span>
              {option.label}
            </button>
          );
        })}
      </div>

      <p className="tw-m-0 tw-mt-4 tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900">Add to</p>
      <div className="tw-flex tw-items-end tw-gap-6 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200 tw-mb-3 tw-max-w-[458px]">
        <button type="button" className={tabClass(tab === "create")} onClick={() => setTab("create")}>
          Create new list
        </button>
        <button type="button" className={tabClass(tab === "existing")} onClick={() => setTab("existing")}>
          Existing list <span className="tw-text-xs tw-bg-primary-purple-50 tw-text-primary-purple-600 tw-px-1.5 tw-py-0.5 tw-rounded-md">{workspaces.length}</span>
        </button>
      </div>

      <div className="tw-max-w-[458px]">
        {tab === "create" ? (
          <>
            <p className="tw-m-0 tw-mb-2 tw-text-sm tw-text-gray-700">Enter the name of the list</p>
            <input ref={nameRef} disabled={busy} value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="ex: high value prospects" className="tw-box-border tw-w-full tw-h-10 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-px-3 tw-text-sm tw-outline-none focus:tw-border-primary-purple-500" />
          </>
        ) : (
          <>
            <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-box-border tw-h-10 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 tw-px-3 tw-mb-3 focus-within:tw-border-primary-purple-500">
              <span aria-hidden="true" className="material-symbols-outlined tw-text-base tw-text-gray-400 tw-leading-none">
                search
              </span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search list" className="tw-w-full tw-h-full tw-border-none tw-p-0 tw-text-sm tw-outline-none" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-max-h-48 tw-overflow-y-auto">
              {!prospectType ? (
                <div className="tw-text-sm tw-text-gray-500">Select buyer or supplier to see your lists</div>
              ) : loadingLists ? (
                <div className="tw-text-sm tw-text-gray-500">Loading workspaces…</div>
              ) : visibleWorkspaces.length === 0 ? (
                <div className="tw-text-sm tw-text-gray-500">No workspaces found</div>
              ) : (
                visibleWorkspaces.map((workspace) => {
                  const selected = workspaceId === workspace.id;
                  return (
                    <button key={workspace.id} type="button" onClick={() => setWorkspaceId(workspace.id ?? "")} className={`tw-w-full tw-text-left tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2 tw-cursor-pointer ${selected ? "tw-border-primary-purple-500 tw-bg-primary-purple-50" : "tw-border-gray-200 tw-bg-white"}`}>
                      <span className={`${radio(selected)} tw-mt-0.5`}>{selected ? <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-purple-500" /> : null}</span>
                      <span className="tw-flex tw-flex-col tw-gap-0.5">
                        <span className="tw-text-sm tw-text-gray-900">{workspace.name || "Workspace"}</span>
                        <span className="tw-text-xs tw-text-gray-500">
                          {countOf(workspace)} companies · {updatedLabel(workspace)}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </ActionCardShell>
  );
}
